/**
 * This file is the same as the partner portal.
 *
 * TODO: Consolidate this file with the partner portal.
 */

import { useId } from 'react';
import { DateField, DateFieldProps } from '@mui/x-date-pickers/DateField';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import {
  focusVisibleStyles,
  standardTransitionStyles,
  focusVisibleStylesNoWrapper,
} from 'design-system/src/sharedStyles';
import Search from '../../assets/icons/search.svg?react';
import { FormLabel } from '../FormLabel/FormLabel';
import moment from 'moment';

import React from 'react';
import styled from 'styled-components';

type SharedProps = {
  id?: string;
  placeholder?: string;
  label: string;
  // If passed, the label will not be visually displayed.
  // Instead, it will be used as placeholder text and accessibly linked to the input.
  // TODO: This behavior is inconsistent across the file and needs to be fixed.
  hideLabel?: boolean;
  hasError?: boolean;
  helperText?: string;
};

type InputSize = 'sm' | 'lg';

type InputProps = SharedProps &
  React.HTMLProps<HTMLInputElement> & {
    inputSize?: InputSize;
  };

type TextAreaProps = SharedProps & React.HTMLProps<HTMLTextAreaElement>;

type DateInputProps = SharedProps & DateFieldProps<moment.Moment>;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { id, label, placeholder = '', type = 'text', hasError, helperText, hideLabel = false, inputSize = 'lg', ...rest },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const helperTextId = useId();

    return (
      <InputContainer>
        <FormLabel htmlFor={inputId} className={hideLabel ? 'sr-only' : ''}>
          {label}
        </FormLabel>
        {helperText && (
          <HelperText id={helperTextId} styledAs="bodyExtraSmallDMSans">
            {helperText}
          </HelperText>
        )}
        <StyledInput
          id={inputId}
          ref={ref}
          type={type}
          placeholder={placeholder}
          $hasError={hasError}
          aria-describedby={helperText ? helperTextId : undefined}
          $size={inputSize}
          {...rest}
        />
      </InputContainer>
    );
  },
);

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ id, label, hasError, helperText, hideLabel = false, ...rest }, ref) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const helperTextId = useId();

    return (
      <InputContainer>
        <FormLabel htmlFor={inputId} className={hideLabel ? 'sr-only' : ''}>
          {label}
        </FormLabel>
        {helperText && (
          <HelperText id={helperTextId} styledAs="bodyExtraSmallDMSans">
            {helperText}
          </HelperText>
        )}
        <StyledDateInput
          id={inputId}
          ref={ref}
          aria-describedby={helperText ? helperTextId : undefined}
          $hasError={hasError}
          format="MM/DD/YYYY"
          {...rest}
        />
      </InputContainer>
    );
  },
);

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ id, label, placeholder = '', helperText, ...rest }, ref) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const helperTextId = useId();

    return (
      <InputContainer>
        <FormLabel htmlFor={inputId}>{label}</FormLabel>
        {helperText && (
          <HelperText id={helperTextId} styledAs="bodySmallDMSans">
            {helperText}
          </HelperText>
        )}
        <StyledTextArea
          id={inputId}
          ref={ref}
          placeholder={placeholder}
          aria-describedby={helperText ? helperTextId : undefined}
          {...rest}
        />
      </InputContainer>
    );
  },
);

const InnerIconContainer = styled.div`
  position: absolute;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0.75rem;
`;
export const SearchField = React.forwardRef<HTMLInputElement, InputProps & { loading?: boolean }>(
  ({ id, label, placeholder = '', hideLabel = false, className, loading = false, inputSize = 'lg', ...rest }, ref) => {
    const generatedId = useId();
    const elementId = id || generatedId;

    return (
      <InputContainer className={className}>
        {!hideLabel && <FormLabel htmlFor={elementId}>{label}</FormLabel>}
        <StyledInput
          id={elementId}
          ref={ref}
          type="text"
          placeholder={hideLabel ? label : placeholder}
          aria-label={hideLabel ? label : undefined}
          autoComplete="off"
          $icon
          $size={inputSize}
          {...rest}
        />
        <InnerIconContainer>
          {loading ? (
            <LoadingSpinner size="14px" />
          ) : (
            <SearchIcon aria-hidden={true} $hideLabel={hideLabel} $size={inputSize} />
          )}
        </InnerIconContainer>
      </InputContainer>
    );
  },
);

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const getPaddingLeft = (size: InputSize, icon: boolean) => {
  if (!icon) {
    return '12px';
  }
  return size === 'lg' ? '42px' : '32px';
};

const StyledInput = styled.input<{ $icon?: boolean; $hasError?: boolean; $size: InputSize }>`
  border: 1px solid var(--grayscale-100);
  background-color: transparent;
  border-radius: var(--border-radius-medium);
  padding: 10px 12px 10px ${({ $size, $icon = false }) => getPaddingLeft($size, $icon)};
  height: ${({ $size }) => ($size === 'sm' ? '32px' : '44px')};
  box-sizing: border-box;
  ${standardTransitionStyles}

  // TODO(design-system): use Typography styles when they've been exported
  font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
  // bodyDMSans and bodySmallDMSans
  font-size: ${({ $size }) => ($size === 'sm' ? '0.875rem' : '0.9375rem')};
  font-weight: 400;
  line-height: ${({ $size }) => ($size === 'sm' ? '128.571%' : '133.333%')};
  color: var(--grayscale-900);

  &::placeholder {
    color: var(--grayscale-300);
  }

  ${focusVisibleStyles}

  &:disabled {
    background-color: var(--grayscale-50);
    cursor: not-allowed;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: var(--red-400);
    
    &:focus-visible {
      border-color: var(--red-400);
      box-shadow: 0 0 2px var(--red-400);
    }
  `}
`;

const StyledDateInput = styled(DateField<moment.Moment>)<{ $hasError?: boolean }>`
  & .MuiInputBase-root {
    border: 1px solid var(--grayscale-100);
    background-color: transparent;
    border-radius: var(--border-radius-medium);
    padding: 10px 12px 10px 12px;
    height: 44px;
    box-sizing: border-box;
    ${standardTransitionStyles}

    // bodyDMSans
    // TODO(design-system): use Typography styles when they've been exported
    font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
    font-size: 0.9375rem;
    font-weight: 400;
    line-height: 133.333%;
    color: var(--grayscale-900);

    & > input {
      // the padding is on MuiInputBase-root
      padding: 0;
    }

    & > fieldset {
      border: none;
    }

    &::placeholder {
      color: var(--grayscale-50);
      // bodyDMSans
      // TODO(design-system): use Typography styles when they've been exported
      font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
      font-size: 0.9375rem;
      font-weight: 400;
      line-height: 133.333%;
    }

    &:disabled {
      background-color: var(--grayscale-50);
      cursor: not-allowed;
    }

    ${({ $hasError }) =>
      $hasError &&
      `
      border-color: var(--red-400);
      
      &:focus-visible {
        border-color: var(--red-400);
        box-shadow: 0 0 2px var(--red-400);
      }
    `}
  }

  & .MuiInputBase-root.Mui-focused {
    ${focusVisibleStylesNoWrapper}
  }
`;

const StyledTextArea = styled.textarea`
  border: 1px solid var(--grayscale-200);
  background-color: transparent;
  border-radius: 0.375rem;
  padding: 0.625rem 0.75rem 0.625rem 0.75rem;
  font-size: 1.0625rem;
  line-height: 1.41;

  &:focus-visible {
    outline: none;
    border: 1px solid var(--purple-700);
    box-shadow: 0 0 2px var(--purple-700);
    background-color: var(--white);
  }
`;

const getSearchIconTop = (hideLabel: boolean, size: InputSize) => {
  if (hideLabel) {
    return size === 'sm' ? '9px' : '12px';
  }
  return size === 'sm' ? '30px' : '36px';
};

const SearchIcon = styled(Search)<{ $hideLabel?: boolean; $size?: InputSize }>`
  position: absolute;
  left: 12px;
  top: ${({ $hideLabel, $size }) => getSearchIconTop($hideLabel ?? false, $size ?? 'lg')};
  width: ${({ $size }) => ($size === 'sm' ? '14px' : '18px')};
  height: ${({ $size }) => ($size === 'sm' ? '14px' : '18px')};
  color: var(--grayscale-900);
`;

const HelperText = styled(Typography)`
  margin-bottom: 4px;
`;
